import { Key, LockSimple, UsersThree } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { AssignmentList } from "@/components/admin/AssignmentList";
import { type AccessVia, type ProductAccess, productAccessQuery, spacesQuery } from "@/components/admin/api";
import { adminError } from "@/components/admin/errors";
import { QueryBoundary, roleName, UserCell, whereText } from "@/components/admin/shared";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  Callout,
  type DataColumn,
  DataTable,
  EmptyState,
  InlineError,
  Radio,
  RadioGroup,
  SectionHeader,
  Select,
  TableSkeleton,
} from "@/components/ui";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";

type AccessSearch = { space?: string; product?: string };

export const Route = createFileRoute("/_app/admin/access")({
  validateSearch: (s: Record<string, unknown>): AccessSearch => ({
    space: typeof s.space === "string" ? s.space : undefined,
    product: typeof s.product === "string" ? s.product : undefined,
  }),
  component: AccessPage,
});

const WHOLE_SPACE = "__space";

function AccessPage() {
  useBreadcrumbs([{ label: m.adm_title(), to: "/admin" }, { label: m.adm_nav_access() }]);
  const spaces = useQuery(spacesQuery);
  const { space: spaceParam, product: productParam } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const list = spaces.data ?? [];
  const space = list.find((s) => s.id === spaceParam) ?? list[0];
  const product = space?.products.find((p) => p.id === productParam);
  const productKey = productParam === WHOLE_SPACE || !product ? WHOLE_SPACE : product.id;

  // Make the URL reflect the resolved selection (first space on a fresh visit), without a history entry.
  useEffect(() => {
    if (space && space.id !== spaceParam) {
      void navigate({ search: { space: space.id, product: productParam }, replace: true });
    }
  }, [space, spaceParam, productParam, navigate]);

  return (
    <Page title={m.adm_nav_access()} icon={Key} meta={m.adm_access_meta()} width="full" className="max-w-5xl">
      <QueryBoundary query={spaces} skeleton={<TableSkeleton rows={3} columns={3} />}>
        {() =>
          !space ? (
            <EmptyState title={m.adm_access_no_spaces_title()} description={m.adm_access_no_spaces_body()} />
          ) : (
            <div className="flex flex-col gap-8">
              <div className="flex flex-wrap items-end gap-3">
                <Select
                  label={m.adm_access_space()}
                  className="w-60"
                  items={list.map((s) => ({ id: s.id, label: s.name }))}
                  selectedKey={space.id}
                  onSelectionChange={(id) => void navigate({ search: { space: id, product: undefined } })}
                />
                <Select
                  label={m.adm_access_product()}
                  className="w-60"
                  items={[
                    { id: WHOLE_SPACE, label: m.adm_access_whole_space() },
                    ...space.products.map((p) => ({
                      id: p.id,
                      label: p.isSpaceProduct ? `${p.name} (${m.space_wide()})` : p.name,
                    })),
                  ]}
                  selectedKey={productKey}
                  onSelectionChange={(id) => void navigate({ search: { space: space.id, product: id } })}
                />
              </div>

              {productKey === WHOLE_SPACE ? (
                <div className="flex flex-col gap-4">
                  <AssignmentList
                    key={space.id}
                    scope="space"
                    scopeId={space.id}
                    where={whereText("space", space.name)}
                    title={m.adm_access_space_title({ name: space.name })}
                    description={m.adm_access_space_body()}
                  />
                </div>
              ) : (
                <ProductAccessView key={productKey} productId={productKey} spaceName={space.name} />
              )}
            </div>
          )
        }
      </QueryBoundary>
    </Page>
  );
}

function ProductAccessView({ productId, spaceName }: { productId: string; spaceName: string }) {
  const access = useQuery(productAccessQuery(productId));
  return (
    <QueryBoundary query={access} skeleton={<TableSkeleton rows={3} columns={3} />} needs="product.admin">
      {(data) => (
        <div className="flex flex-col gap-8">
          <Visibility data={data} spaceName={spaceName} />
          <AssignmentList
            scope="product"
            scopeId={productId}
            where={whereText("product", data.product.name)}
            title={m.adm_access_grants_title()}
            description={m.adm_access_grants_body()}
          />
          <Effective data={data} />
        </div>
      )}
    </QueryBoundary>
  );
}

function Visibility({ data, spaceName }: { data: ProductAccess; spaceName: string }) {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (visibility: "space" | "private") =>
      api(`/api/v1/admin/products/${data.product.id}/visibility`, { method: "PUT", body: { visibility } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin"] });
      toast.show({ title: m.adm_access_visibility_saved(), tone: "success" });
    },
  });
  return (
    <section aria-labelledby="acc-visibility" className="flex flex-col gap-3">
      <SectionHeader
        id="acc-visibility"
        title={m.adm_access_visibility_title()}
        description={m.adm_access_visibility_body()}
      />
      {set.isError ? <InlineError message={adminError(set.error)} /> : null}
      <RadioGroup
        label={m.adm_access_visibility_title()}
        labelHidden
        className="max-w-xl"
        value={data.product.visibility}
        onChange={(v) => set.mutate(v as "space" | "private")}
        isDisabled={set.isPending}
      >
        <Radio
          value="space"
          label={m.adm_access_vis_space({ space: spaceName })}
          description={m.adm_access_vis_space_hint()}
        />
        <Radio value="private" label={m.adm_access_vis_private()} description={m.adm_access_vis_private_hint()} />
      </RadioGroup>
      {data.product.visibility === "private" ? (
        <Callout tone="info" className="max-w-xl">
          {m.adm_access_private_note()}
        </Callout>
      ) : null}
    </section>
  );
}

function viaLabel(v: AccessVia): string {
  if (v.source === "owner") return m.adm_access_via_owner();
  const role = v.role ? roleName({ name: v.role.name }) : "";
  const scope =
    v.source === "instance"
      ? m.adm_access_scope_instance()
      : v.source === "space"
        ? m.adm_access_scope_space()
        : m.adm_access_scope_product();
  return v.group
    ? m.adm_access_via_group({ role, scope, group: v.group.name })
    : m.adm_access_via_direct({ role, scope });
}

function Effective({ data }: { data: ProductAccess }) {
  type Row = ProductAccess["people"][number] & { id: string };
  const rows: Row[] = data.people.map((p) => ({ ...p, id: p.user.id }));
  const columns: DataColumn<Row>[] = [
    {
      id: "person",
      header: m.adm_access_col_person(),
      isRowHeader: true,
      className: "min-w-52",
      cell: (r) => (
        <UserCell
          name={r.user.name}
          email={r.user.kind === "bot" ? m.adm_bot() : r.user.email}
          seed={r.user.id}
          bot={r.user.kind === "bot"}
          disabled={!r.active}
        />
      ),
    },
    {
      id: "via",
      header: m.adm_access_col_via(),
      className: "min-w-56",
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.via.map((v) => (
            <Badge
              key={`${v.assignmentId ?? v.source}-${v.group?.id ?? ""}-${v.role?.slug ?? ""}`}
              icon={v.group ? UsersThree : v.source === "owner" ? LockSimple : undefined}
            >
              {viaLabel(v)}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: "can",
      header: m.adm_access_col_can(),
      className: "w-36",
      cell: (r) =>
        !r.active ? (
          <span className="text-fg-subtle">{m.adm_status_disabled()}</span>
        ) : (
          <div className="flex gap-1">
            {r.canRead ? <Badge tone="success">{m.adm_access_can_read()}</Badge> : null}
            {r.canAdmin ? <Badge tone="accent">{m.adm_access_can_admin()}</Badge> : null}
          </div>
        ),
    },
  ];
  return (
    <section aria-labelledby="acc-effective" className="flex flex-col gap-3">
      <SectionHeader
        id="acc-effective"
        title={m.adm_access_effective_title()}
        description={m.adm_access_effective_body()}
      />
      <DataTable
        label={m.adm_access_effective_title()}
        columns={columns}
        rows={rows}
        renderEmptyState={() => (
          <EmptyState title={m.adm_access_effective_none_title()} description={m.adm_access_effective_none_body()} />
        )}
      />
    </section>
  );
}
